import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ethers } from 'ethers';
import PaymentVaultABI from 'abis/PaymentVault';
import { createRevenueClaim, getRevenueClaims } from 'helpers/revenueClaim.api';
import { buttonStyles } from '../css/commonStyles';
import {
  colors,
  confirmBtnSx,
  pageTitleSx,
  previewSummaryCardSx,
  previewSummaryGridSx,
  previewSummaryLabelSx,
  previewSummaryValueSx,
  tabHeaderRowSx,
  tableCardStyles,
  tableContainerStyles,
} from '../css/revenueItems.styles';
import { formatDateTime } from '../utils/revenueItems.utils';
import { formatRevenueSharingErrorMessage, isTxUserRejection } from '../utils/revenueSharing.utils';
import { ensureRevenueSharingMessageLayer, revenueSharingMessage } from '../utils/revenueSharingMessage';
import { ensureCorrectChain } from '../utils/vaultChain.utils';
import PaymentsPagination from './PaymentsPagination';
import { buildPaginationItems } from '../utils/revenueItems.utils';

const formatUsdt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '0');
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 });
};

const txHashCellSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  wordBreak: 'break-all',
  lineHeight: 1.45,
};

const ClaimRevenueTab = ({ currentDao }) => {
  const [walletAddress, setWalletAddress] = useState('');
  const [claimable, setClaimable] = useState('0');
  const [tokenAddress, setTokenAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [hasMetaMask, setHasMetaMask] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });

  const vaultAddress = (currentDao?.vaultAddress || '').trim();
  const hasVault = /^0x[0-9a-fA-F]{40}$/.test(vaultAddress);

  const loadHistory = useCallback(async () => {
    if (!currentDao?._id || !walletAddress) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const res = await getRevenueClaims({
        daoId: currentDao._id,
        recipientAddress: walletAddress.toLowerCase(),
        page: historyPage,
        limit: 10,
      });
      setHistory(res?.data || []);
      setHistoryPagination({
        page: res?.pagination?.page || historyPage,
        limit: res?.pagination?.limit || 10,
        totalItems: res?.pagination?.totalItems || 0,
        totalPages: res?.pagination?.totalPages || 1,
      });
    } catch (err) {
      setHistory([]);
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Failed to load claim history'));
    } finally {
      setHistoryLoading(false);
    }
  }, [currentDao?._id, walletAddress, historyPage]);

  const loadClaimable = useCallback(async () => {
    if (!hasVault || !window.ethereum) return;
    setLoading(true);
    try {
      await ensureCorrectChain(currentDao?.chainId);
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const accounts = await provider.listAccounts();
      const account = accounts[0];
      if (!account) {
        setWalletAddress('');
        setClaimable('0');
        setTokenAddress('');
        return;
      }
      setWalletAddress(account);
      const vault = new ethers.Contract(vaultAddress, PaymentVaultABI, provider);
      const [raw, tokenAddr] = await Promise.all([
        vault.getTotalClaimable(account),
        vault.acceptedToken(),
      ]);
      setTokenAddress(tokenAddr);
      const token = new ethers.Contract(
        tokenAddr,
        ['function decimals() view returns (uint8)'],
        provider,
      );
      const decimals = await token.decimals();
      setClaimable(ethers.utils.formatUnits(raw, decimals));
    } catch (err) {
      setClaimable('0');
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Failed to load claimable balance'));
    } finally {
      setLoading(false);
    }
  }, [currentDao?.chainId, hasVault, vaultAddress]);

  const syncWalletAccount = useCallback(async () => {
    if (!window.ethereum) {
      setWalletAddress('');
      setClaimable('0');
      return;
    }
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    const accounts = await provider.listAccounts();
    setWalletAddress(accounts[0] || '');
    if (!hasVault) {
      setClaimable('0');
      setTokenAddress('');
    }
  }, [hasVault]);

  useEffect(() => {
    setHasMetaMask(typeof window.ethereum !== 'undefined');
    ensureRevenueSharingMessageLayer();
    if (!window.ethereum) return undefined;

    if (hasVault) {
      loadClaimable();
    } else {
      syncWalletAccount();
    }

    const onAccounts = () => {
      setHistoryPage(1);
      if (hasVault) loadClaimable();
      else syncWalletAccount();
    };
    const onChain = () => {
      if (hasVault) loadClaimable();
    };
    window.ethereum.on('accountsChanged', onAccounts);
    window.ethereum.on('chainChanged', onChain);
    return () => {
      window.ethereum.removeListener('accountsChanged', onAccounts);
      window.ethereum.removeListener('chainChanged', onChain);
    };
  }, [hasVault, loadClaimable, syncWalletAccount]);

  useEffect(() => {
    if (walletAddress) loadHistory();
  }, [walletAddress, historyPage, loadHistory]);

  const connectWallet = async () => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      if (hasVault) {
        await loadClaimable();
      } else {
        await syncWalletAccount();
      }
    } catch (err) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Failed to connect wallet'));
    }
  };

  const handleClaim = async () => {
    if (!hasVault || !walletAddress) return;
    const amountNum = Number(claimable);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      revenueSharingMessage.warning('You have no claimable revenue for this wallet.');
      return;
    }

    const amountClaimed = claimable;
    setClaiming(true);
    try {
      await ensureCorrectChain(currentDao?.chainId);
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signer = provider.getSigner();
      const vault = new ethers.Contract(vaultAddress, PaymentVaultABI, signer);

      try {
        await vault.callStatic.claim();
      } catch (staticErr) {
        revenueSharingMessage.error(formatRevenueSharingErrorMessage(staticErr, 'Claim would fail'));
        return;
      }

      const tx = await vault.claim();
      const receipt = await tx.wait();
      const txHash = receipt?.transactionHash || tx.hash;

      try {
        await createRevenueClaim({
          daoId: currentDao._id,
          chainId: currentDao.chainId,
          recipientAddress: walletAddress.toLowerCase(),
          amount: amountClaimed,
          txHash,
          tokenAddress,
          vaultAddress: vaultAddress.toLowerCase(),
        });
      } catch (apiErr) {
        revenueSharingMessage.warning(
          'Claim succeeded on-chain but saving history failed. Please contact support with your transaction hash.',
        );
      }

      revenueSharingMessage.success('Revenue claimed successfully!');
      setHistoryPage(1);
      await loadClaimable();
      await loadHistory();
    } catch (err) {
      const msg = isTxUserRejection(err)
        ? 'Transaction was rejected.'
        : formatRevenueSharingErrorMessage(err, 'Claim failed');
      if (isTxUserRejection(err)) revenueSharingMessage.warning(msg);
      else revenueSharingMessage.error(msg);
    } finally {
      setClaiming(false);
    }
  };

  const historyPaginationItems = useMemo(
    () => buildPaginationItems(historyPagination.page || 1, historyPagination.totalPages || 1),
    [historyPagination.page, historyPagination.totalPages],
  );

  const historyPaginationLabel = useMemo(() => {
    if (!historyPagination.totalItems) {
      return historyLoading ? 'Loading claim history...' : 'No claims yet for this wallet';
    }
    const start = (historyPagination.page - 1) * historyPagination.limit + 1;
    const end = Math.min(start + history.length - 1, historyPagination.totalItems);
    return `Showing ${start}-${end} of ${historyPagination.totalItems} claims`;
  }, [historyLoading, historyPagination, history.length]);

  const canClaim = hasVault && walletAddress && Number(claimable) > 0 && !loading && !claiming;

  if (!hasMetaMask) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography sx={{ color: colors.textSecondary, fontFamily: 'Questrial, sans-serif' }}>
          Install MetaMask to claim revenue from this DAO vault.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={tabHeaderRowSx}>
        <Typography variant="h6" sx={pageTitleSx}>
          Claim revenue
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {!walletAddress && (
            <Button
              variant="outlined"
              onClick={connectWallet}
              sx={{
                color: colors.accent,
                borderColor: colors.accent,
                borderRadius: '12px',
                textTransform: 'none',
                fontFamily: 'Questrial, sans-serif',
              }}
            >
              Connect wallet
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleClaim}
            disabled={!canClaim}
            sx={[buttonStyles, confirmBtnSx, { minWidth: 160 }]}
          >
            {claiming ? <CircularProgress size={22} sx={{ color: '#282f38' }} /> : 'Claim'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ ...previewSummaryGridSx, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 3 }}>
        <Box sx={previewSummaryCardSx}>
          <Typography sx={previewSummaryLabelSx}>Claimable (USDT)</Typography>
          <Typography sx={{ ...previewSummaryValueSx, color: colors.accent, fontSize: '1.35rem' }}>
            {hasVault && loading ? '…' : formatUsdt(claimable)}
          </Typography>
        </Box>
        <Box sx={previewSummaryCardSx}>
          <Typography sx={previewSummaryLabelSx}>Your wallet</Typography>
          <Typography
            sx={{
              ...previewSummaryValueSx,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}
          >
            {walletAddress || 'Not connected'}
          </Typography>
        </Box>
      </Box>

      <Typography sx={{ ...previewSummaryLabelSx, mb: 1 }}>Claim history</Typography>
      <Box sx={tableCardStyles}>
        <TableContainer component={Box} sx={tableContainerStyles}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Claimed at</TableCell>
                <TableCell align="right">Amount (USDT)</TableCell>
                <TableCell>Transaction hash</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyLoading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} sx={{ color: colors.accent }} />
                  </TableCell>
                </TableRow>
              ) : history.length ? (
                history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDateTime(row.claimedAt || row.createdAt)}</TableCell>
                    <TableCell align="right">{formatUsdt(row.amount)}</TableCell>
                    <TableCell>
                      <Typography sx={txHashCellSx}>{row.txHash || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                    {walletAddress ? 'No claims recorded for this wallet yet.' : 'Connect wallet to view history.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {walletAddress && (
          <PaymentsPagination
            pagination={historyPagination}
            paginationItems={historyPaginationItems}
            paginationLabel={historyPaginationLabel}
            onPageChange={setHistoryPage}
          />
        )}
      </Box>
    </Box>
  );
};

export default ClaimRevenueTab;
